import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import {
  deleteLocalStorageValue,
  getLocalStorageValue,
  prefixStorageKey,
  setLocalStorageValue,
} from '@antimatterhq/ui/utils/localStorage';
import { useFrameState } from '@/providers/frame-state';

interface InstanceIdContextType {
  instanceId: string | null;
  clearInstanceId: () => void;
}

const InstanceIdContext = createContext<InstanceIdContextType>({
  instanceId: null,
  clearInstanceId: () => {},
});

function getInstanceIDFromLocalStorage() {
  return getLocalStorageValue<string>(prefixStorageKey('instanceID')) ?? null;
}

function clearStoredInstanceIDs(): void {
  deleteLocalStorageValue(prefixStorageKey('instanceID'));
}

function getInstanceIDFromQueryParams() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('instanceId')) {
    const instanceID = params.get('instanceId');
    return instanceID;
  }

  return null;
}

function getInstanceID(instanceIdFromParent: string | null) {
  const instanceID =
    instanceIdFromParent ??
    getInstanceIDFromQueryParams() ??
    getInstanceIDFromLocalStorage();

  return instanceID;
}

export const InstanceIdProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { instanceId: instanceIdFromParent } = useFrameState();
  const [instanceId, setInstanceId] = useState<string | null>(
    getInstanceID(instanceIdFromParent),
  );

  useEffect(() => {
    const localStorageInstanceID = getInstanceIDFromLocalStorage();

    if (localStorageInstanceID !== instanceId) {
      setLocalStorageValue(prefixStorageKey('instanceID'), instanceId);
    }
  }, [instanceId]);

  const clearInstanceId = useCallback(() => {
    clearStoredInstanceIDs();
    setInstanceId(null);
  }, []);

  const value = useMemo(
    () => ({ instanceId, clearInstanceId }),
    [instanceId, clearInstanceId],
  );

  return (
    <InstanceIdContext.Provider value={value}>
      {instanceId ? (
        children
      ) : (
        <div className="flex h-screen w-screen flex-col items-center justify-center">
          <GlobalError
            title="Something went wrong"
            description={
              'Unable to authenticate. A Databricks instance ID is required'
            }
            isRetryable={false}
          />
        </div>
      )}
    </InstanceIdContext.Provider>
  );
};

export const useInstanceId = () => {
  const instanceId = useContext(InstanceIdContext);

  if (instanceId == null) {
    throw new Error('InstanceIdContext not found');
  }

  return instanceId;
};
